import { OdinIcon, OdinIconSize, Popover, PopoverContent, PopoverTrigger } from '@myosh/odin-components';
import * as htmlToImage from 'html-to-image';
import { t } from 'i18next';
import jsPDF from 'jspdf';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import MyoshLogo from '../../assets/images/myosh_logo.png';
import { odinIconStyle } from '../../constants/common-style-constants';
import { CHART_TYPE } from '../../constants/enums';
import type { ComparedFrLineResponse } from '../../data-transformer/compared-fr-line-chart/compared-fr-line.interface';
import { useConfiguredDateRanges } from '../../hooks/useConfiguredDateRanges';
import type { CustomFiltersType } from '../../redux/config/CustomFiltersApi';
import type {
  BasicDataChartResponse,
  ChartColumnLineResponse,
  ChartDataKPI,
  ChartResponse,
  ColumnKpiPercentageResponse,
  ColumnRateResponse,
  MapChartData,
  NCRLineChartResponse,
} from '../../redux/config/chart-data';
import { useGetUserAppliedFiltersQuery } from '../../redux/config/filters-api';
import type { LayoutWidget } from '../../redux/config/layout';
import { useUpdateLayoutWidgetDataMutation } from '../../redux/config/layout-widget';
import type {
  CrossTabReportProps,
  GroupedTableType,
  TableDataResponse,
} from '../../redux/config/table-data.interfaces';
import { useDeleteLayoutWidgetMutation, useGetWidgetInsightsEnabledQuery } from '../../redux/config/widgetApi';
import { selectActiveTabId } from '../../redux/slices/active-layout-tab-id-slice';
import { getTabs, getWidgets } from '../../redux/slices/layout-tabs-slice';
import { JobStatus, selectSubmittedJobs } from '../../redux/slices/running-jobs';
import { addSidePanelItem } from '../../redux/slices/side-panel-slice';
import {
  selectStagedWidgetDimensions,
  setWidgetWidthHeight,
  stageWidgetDimensions,
} from '../../redux/slices/update-width-height-of-chart';
import type { ChartDifferenceProps } from '../Charts/chart.interface';
import { useExportFile } from '../Charts/hooks/use-export-file';
import { useIsEditableMode } from '../Charts/hooks/useIsEditableMode';
import { OdinCustomFilters } from '../CustomFilterComponent/CustomFilterComponent';
import WidgetSettingsDialog from '../WidgetSettingWindow/widget-settings-dialog';
import type { WidgetConfigsGenericWrapper } from './chart-generic-wrapper';
import ConfirmationWindow from './confirmation-window';
import type { MenuItem } from './menu-item';
import SettingsMenu from './menu-item';

/**
 * Widget chart data can have various shapes depending on the chart type.
 *
 * Known shapes:
 * - `ChartResponse[]` - Pie, Line, Bar, Column charts
 * - `ChartDataKPI` - KPI charts (has `basicValue` array property)
 * - `ChartColumnLineResponse` - Column/Line combo charts
 * - `ComparedFrLineResponse` - Compared FR line charts
 * - `ColumnRateResponse[]` - Column rate charts
 * - `ColumnKpiPercentageResponse[]` - KPI percentage charts
 * - `MapChartData[]` - Map widgets
 * - `NCRLineChartResponse` - NCR aging line charts
 * - `BasicDataChartResponse[]` - Basic data arrays
 * - `CrossTabReportProps` - Cross-tab report tables
 * - `ChartDifferenceProps` - Difference charts
 * - `GroupedTableType` - Grouped table data (injury site)
 * - `TableDataResponse` - Table rate data
 *
 * Use the type guards below to safely access data:
 * - isChartDataKPI() - checks for { basicValue: unknown[] } shape (ChartDataKPI)
 * - hasWidgetData() - checks if there's any data to display
 * - getWidgetDataBody() - extracts the data array regardless of shape
 */
export type WidgetChartData =
  | ChartResponse[]
  | ChartDataKPI
  | ChartColumnLineResponse
  | ComparedFrLineResponse
  | ColumnRateResponse[]
  | ColumnKpiPercentageResponse[]
  | MapChartData[]
  | NCRLineChartResponse
  | BasicDataChartResponse[]
  | CrossTabReportProps
  | ChartDifferenceProps
  | GroupedTableType
  | TableDataResponse
  | undefined;

/** Type guard to check if widgetChartData is ChartDataKPI (has basicValue array property) */
const isChartDataKPI = (data: WidgetChartData): data is ChartDataKPI =>
  typeof data === 'object' && data !== null && 'basicValue' in data && Array.isArray(data.basicValue);

/** Gets the data array from widgetChartData, handling both formats */
const getWidgetDataBody = (data: WidgetChartData): WidgetChartData | BasicDataChartResponse[] => {
  if (isChartDataKPI(data)) {
    return data.basicValue;
  }
  return data;
};

/** Checks if widgetChartData has any data */
const hasWidgetData = (data: WidgetChartData): boolean => {
  if (isChartDataKPI(data)) {
    return (data.basicValue?.length ?? 0) > 0;
  }
  return Array.isArray(data) && data.length > 0;
};

export interface ChartBottomPanelInterface {
  widgetId: string;
  refetch: () => void;
  widgetConfiguration?: WidgetConfigsGenericWrapper;
  isTable?: boolean;
  widgetChartData: WidgetChartData;
}

const canGenerateAiInsight = (type?: CHART_TYPE) => {
  if (
    type === CHART_TYPE.ChartBar ||
    type === CHART_TYPE.ChartColumn ||
    type === CHART_TYPE.ChartLine ||
    type === CHART_TYPE.ChartPie
  ) {
    return true;
  }
  return false;
};

const PDF_HORIZONTAL_MARGIN = 20;
const PDF_VERTICAL_MARGIN = 60;

const BACKGROUND_EXPORT_WIDGET_TYPES = [
  'TABLE',
  'TABLE_INJURY_FREE_DAYS',
  'MANAGEMENT_TABLE',
  'CROSS_TAB_REPORT',
  'TABLE_ROLLING_TRIFR',
  'TABLE_RATE',
  'INJURY_NUMBER_BY_SITE_TABLE',
];

export default function ChartBottomPanel({
  widgetId,
  refetch,
  isTable,
  widgetConfiguration,
  widgetChartData,
}: ChartBottomPanelInterface) {
  const [exportMenuOpen, setExportMenuOpen] = useState<boolean>(false);
  const [settingsOpened, setSettingsOpened] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [showFooter, setShowFooter] = useState<boolean>(true);
  const [customFiltersData, setCustomFiltersData] = useState<Array<CustomFiltersType>>([]);

  const [deleteWidget, { isLoading: isDeleting }] = useDeleteLayoutWidgetMutation();
  const isEditable = useIsEditableMode();
  const { data: aiEnabled, isFetching: isConfigFetching } = useGetWidgetInsightsEnabledQuery();
  const dispatch = useDispatch();
  const { exportWidgetHandler } = useExportFile({ widgetId });
  const { data: userAppliedFilters } = useGetUserAppliedFiltersQuery();
  const dateRanges = useConfiguredDateRanges();

  const layoutTabs = useSelector(getTabs);
  const runningJobs = useSelector(selectSubmittedJobs);
  const layoutTabWidgets = useSelector(getWidgets);
  const stagedWidgetDimensions = useSelector(selectStagedWidgetDimensions);
  const activeTabId = useSelector(selectActiveTabId);
  const layoutWidget = useMemo<LayoutWidget | undefined>(() => {
    const nextLayoutWidget = layoutTabWidgets.find((widget) => +widget.widget.id === +widgetId);
    if (!nextLayoutWidget) {
      return undefined;
    }

    const stagedDimensions = stagedWidgetDimensions[nextLayoutWidget.id];
    return stagedDimensions
      ? {
          ...nextLayoutWidget,
          ...stagedDimensions,
        }
      : nextLayoutWidget;
  }, [layoutTabWidgets, stagedWidgetDimensions, widgetId]);

  const runningJobsRef = useRef(runningJobs);

  const layoutProps = useMemo(() => {
    if (layoutTabs) {
      return layoutTabs.find((tab) => tab.id === activeTabId);
    }
  }, [activeTabId, layoutTabs]);

  const isAvailableToUser: boolean = useMemo(
    () => (layoutProps?.canModify || layoutProps?.isUserLayout ? isEditable : false),
    [layoutProps, isEditable]
  );

  const captureScreenshot = async () => {
    const widgetLayoutId = String(layoutWidget?.id);
    const node = document.getElementById(widgetLayoutId);

    if (node) {
      toast.loading(t('exportInProgress'));

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      const logoProperties = doc.getImageProperties(MyoshLogo);
      const rescaleFactor = 0.1;
      const logoWidth = logoProperties.width * rescaleFactor;
      const logoHeight = logoProperties.height * rescaleFactor;
      doc.addImage(MyoshLogo, 'PNG', 5, 2.5, logoWidth, logoHeight);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);

      doc.text(`${widgetConfiguration?.title}`, 20, 20);

      // Add date range to the PDF, preferring view-specific filters if available
      let dateRange = '';
      const { fromDate, toDate } = userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView || {};
      const dateFilterRangeType =
        userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView?.dateFilterRangeType;
      if (dateFilterRangeType) {
        const dates = dateRanges?.find((range) => range.id === dateFilterRangeType);
        dateRange = dates ? `${dates.from} - ${dates.to}` : '';
      } else if (fromDate && toDate) {
        dateRange = `${fromDate} - ${toDate}`;
      }

      if (dateRange) {
        const textWidth = doc.getTextWidth(dateRange);
        doc.text(dateRange, pageWidth - textWidth - 20, 20);
      }

      try {
        const dataUrl = await htmlToImage.toPng(node, {
          cacheBust: true,
          backgroundColor: 'white',
          pixelRatio: 2,
        });

        const imgProps = doc.getImageProperties(dataUrl);

        const pdfWidth = pageWidth - PDF_HORIZONTAL_MARGIN;
        const pdfHeight = doc.internal.pageSize.getHeight() - PDF_VERTICAL_MARGIN;

        let imgWidth = imgProps.width;
        let imgHeight = imgProps.height;

        // scale down proportionally
        const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
        imgWidth *= ratio;
        imgHeight *= ratio;

        const x = (pageWidth - imgWidth) / 2;
        const y = 30;

        doc.addImage(dataUrl, 'PNG', x, y, imgWidth, imgHeight);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);

        let textY = y + imgHeight + 15;

        customFiltersData
          .filter((f) => f.currentValues && f.currentValues.length > 0)
          .forEach((field) => {
            const label = `${field.field}: ${(field.currentValues ?? []).join(', ')}`;
            doc.text(label, 20, textY);
            textY += 7;
          });

        doc.save(`${widgetConfiguration?.title}`);
        toast.dismiss();
        toast.success(t('exportComplete'));
      } catch (_error) {
        toast.dismiss();
        toast.error(t('exportFailed'));
      } finally {
        setShowFooter(true);
      }
    } else {
      toast.error(t('exportFailed'));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: aiEnabled, configOverlayOpened, handleClickAiInsight, handleClickRecordInsight, isConfigFetching, onEditDimension, widgetChartData, and widgetConfiguration props are intentionally excluded to prevent excessive re-renders
  const menuOptions: MenuItem[] = useMemo(
    () =>
      [
        {
          title: 'Record Insight',
          isSupported: true,
          isAvailableToUser:
            !isConfigFetching &&
            Boolean(aiEnabled) &&
            !isEmpty(widgetConfiguration?.hoursWorkedAIConfig) &&
            !isEmpty(widgetConfiguration?.injuryFrequencyAIConfig),
          icon: 'ClipboardList',
          clickHandler:
            widgetConfiguration?.id && widgetConfiguration?.title
              ? () => handleClickRecordInsight(widgetConfiguration.id, widgetConfiguration.title)
              : undefined,
        },
        {
          title: 'AI Insight',
          isSupported: true,
          isAvailableToUser:
            !isConfigFetching &&
            Boolean(aiEnabled) &&
            canGenerateAiInsight(widgetConfiguration?.type) &&
            hasWidgetData(widgetChartData),
          icon: 'Sparkles',
          clickHandler: () => handleClickAiInsight(),
        },
        {
          title: t('settings'),
          isSupported: true,
          isAvailableToUser: layoutProps?.canAdmin ?? false,
          icon: 'Settings',
          clickHandler: () => setSettingsOpened(!settingsOpened),
        },
        {
          title: t('export'),
          icon: 'Download',
          isSupported: true,
          isAvailableToUser: true,
          //availability depends on widget's type not permissions
          clickHandler: () => setExportMenuOpen(!exportMenuOpen),
        },
        {
          title: layoutWidget?.width === '100%' ? t('singleWidth') : t('doubleWidth'),
          isSupported: true,
          isAvailableToUser,
          icon: 'SquareSplitHorizontal',
          clickHandler: () => onEditDimension(false, true),
        },
        {
          title: layoutWidget?.height === '100%' ? t('singleHeight') : t('doubleHeight'),
          isSupported: true,
          isAvailableToUser,
          icon: 'SquareSplitVertical',
          clickHandler: () => onEditDimension(true, false),
        },
        {
          title: t('delete'),
          isSupported: true,
          isAvailableToUser,
          icon: 'Trash',
          clickHandler: () => setShowSaveModal(true),
        },
      ] satisfies MenuItem[],
    [settingsOpened, layoutProps, layoutWidget, isAvailableToUser, exportWidgetHandler, isTable]
  );

  const exportSubMenuItems = [
    {
      title: 'CSV',
      clickHandler: () => exportWidgetHandler('CSV', true),
      isSupported: false,
      isAvailableToUser: true,
    },
    {
      title: 'Excel',
      clickHandler: () => exportWidgetHandler('EXCEL', true),
      isSupported: true,
      isAvailableToUser: isTable,
    },
    {
      title: 'PDF',
      clickHandler: () => {
        if (BACKGROUND_EXPORT_WIDGET_TYPES.includes(String(widgetConfiguration?.type))) {
          exportWidgetHandler('PDF', false);
        } else {
          setExportMenuOpen(false);
          setShowFooter(false);
          setTimeout(() => {
            captureScreenshot();
          }, 200);
        }
      },
      isSupported: true,
      isAvailableToUser: true,
    },
  ];

  const onDeleteHandler = () => {
    const widgetLayoutId = layoutWidget?.id;
    if (widgetLayoutId) {
      const widgetRef = document.getElementById(widgetLayoutId);
      deleteWidget(widgetLayoutId)
        .then(() => {
          toast.success(t('deleteWidgetSuccess'));
          onHideModal();
        })
        .catch(() => {
          toast.error(t('deleteWidgetFailure'));
        });
      if (widgetRef) {
        widgetRef.remove();
      }
    }
  };

  const onEditDimension = useCallback(
    (updateHeight: boolean, updateWidth: boolean) => {
      if (layoutProps && layoutWidget) {
        const { height, width } = layoutWidget;
        const updatedData = {
          ...layoutWidget,
          width: updateWidth ? (width === '100%' ? '50%' : '100%') : width,
          height: updateHeight ? (height === '100%' ? '50%' : '100%') : height,
        };
        dispatch(stageWidgetDimensions({ id: updatedData.id, width: updatedData.width, height: updatedData.height }));
        dispatch(
          setWidgetWidthHeight({
            id: updatedData.id,
            updateHeight,
            updateWidth,
          })
        );
      }
    },
    [layoutProps, layoutWidget, dispatch]
  );

  const onHideModal = () => {
    setShowSaveModal(false);
  };

  const handleClickAiInsight = () => {
    dispatch(
      addSidePanelItem({
        key: `ai-insight-${widgetId}`,
        type: 'AI_INSIGHT',
        widgetId: widgetId,
        title: widgetConfiguration?.title ? widgetConfiguration.title : '',
        icon: 'Sparkles',
        chartData: getWidgetDataBody(widgetChartData) as BasicDataChartResponse[],
        job: {
          status: JobStatus.NotStarted,
        },
      })
    );
  };

  const handleClickRecordInsight = useCallback(async (widgetId: string, widgetTitle: string) => {
    dispatch(
      addSidePanelItem({
        key: `record-insight-${widgetId}`,
        type: 'RECORD_INSIGHT',
        widgetId: widgetId,
        title: widgetTitle,
        icon: 'ClipboardList',
      })
    );
  }, []);

  // Keep the ref always up to date
  useEffect(() => {
    runningJobsRef.current = runningJobs;
  }, [runningJobs]);

  return (
    <>
      <div className="mt-auto flex flex-row justify-between">
        {layoutWidget?.widget?.widgetData?.customFilterField && showFooter ? (
          <OdinCustomFilters
            id={widgetId}
            customFiltersData={customFiltersData}
            setCustomFiltersData={setCustomFiltersData}
            onFilterStateChanged={refetch}
          />
        ) : null}
        <div className="ml-auto flex flex-row justify-end gap-2">
          {menuOptions
            .filter((option) => option.isAvailableToUser)
            .map((menuOption) => {
              if (menuOption.title === t('export')) {
                return (
                  <Popover key={menuOption.title} open={exportMenuOpen} onOpenChange={setExportMenuOpen}>
                    <PopoverTrigger title={menuOption.title} className="m-0.5 ml-auto flex-none">
                      <OdinIcon
                        icon={menuOption.icon ?? 'Download'}
                        size={OdinIconSize.Small}
                        className={showFooter ? odinIconStyle : `text-mono-1`}
                      />
                    </PopoverTrigger>
                    <PopoverContent side="top" align="end" className="w-32">
                      <SettingsMenu items={exportSubMenuItems} />
                    </PopoverContent>
                  </Popover>
                );
              }

              return (
                <div
                  key={menuOption.title}
                  onClick={menuOption.clickHandler}
                  className="m-0.5 ml-auto flex-none"
                  title={menuOption.title}
                >
                  <OdinIcon
                    icon={menuOption.icon ?? 'ClipboardList'}
                    size={OdinIconSize.Small}
                    className={showFooter ? odinIconStyle : `text-mono-1`}
                  />
                </div>
              );
            })}
        </div>
      </div>

      {widgetConfiguration && (
        <WidgetSettingsDialog
          aiEnabled={Boolean(aiEnabled)}
          widgetConfiguration={widgetConfiguration}
          visible={settingsOpened}
          onDialogHidden={() => setSettingsOpened(false)}
        />
      )}
      <ConfirmationWindow
        onSaveHandler={onDeleteHandler}
        showWindow={showSaveModal}
        onDiscardChanges={{
          onHideModal,
          onDiscard: onHideModal,
        }}
        title={isDeleting ? t('deleting') : t('deleteWidget')}
        text={t('confirmDelete')}
        discard={t('no')}
        save={t('yes')}
      />
    </>
  );
}
